import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { getTopBarSize } from './Fizzbuzz';
import AvatarImg from './AvatarImg';
import { useUser } from '../context/UserContext';
import './TopBar.css';

interface Props {
  onLogout: () => void;
  onOpenSearch: () => void;
}

// Single source of truth for the page nav. To add a new page later, append
// one row here and add the matching <Route> in App.tsx. `soon` items render
// disabled with a "soon" pill.
interface NavItem {
  label: string;
  to: string;
  soon?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'home',  to: '/'      },
  { label: 'about', to: '/about' },
  { label: 'blog',  to: '/blogs' },
];

const TopBar: React.FC<Props> = ({ onLogout, onOpenSearch }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    username, level, xp, xpToNextLevel, coins, streak, xpPercent,
    pendingInCount,
  } = useUser();
  const fbSize = getTopBarSize(level);

  // Mobile drawer. Closed by default; routes auto-close it so the user lands
  // on the new page without an open sheet on top.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open so the page underneath doesn't
  // bleed-scroll behind the sheet.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  const renderNavLinks = (onPick?: () => void) =>
    NAV_ITEMS.map((item) =>
      item.soon ? (
        <span key={item.to} className="topbar-nav-link soon" aria-disabled="true">
          {item.label}
          <span className="sk-pill" style={{ fontSize: 9, padding: '0 6px' }}>soon</span>
        </span>
      ) : (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          className={({ isActive }) => `topbar-nav-link${isActive ? ' active' : ''}`}
          onClick={onPick}
        >
          {item.label}
        </NavLink>
      ),
    );

  return (
    <header className="topbar">
      <Link to="/" className="topbar-brand">
        <AvatarImg size={fbSize} mood="happy" />
        <div>
          <div className="topbar-brand-name">study buddy</div>
          <div className="topbar-brand-sub">online interview prep</div>
        </div>
      </Link>

      {/* Desktop nav strip — hidden on mobile via CSS */}
      <nav className="topbar-nav">
        {renderNavLinks()}
      </nav>

      <div className="topbar-stats">
        <button
          className="sk-pill"
          style={{ cursor: 'pointer', border: '1.4px solid var(--ink)', fontFamily: 'inherit' }}
          onClick={onOpenSearch}
          title="Search problems (⌘K / Ctrl+K)"
          aria-label="Search problems"
        >
          🔍
        </button>

        <span className="sk-pill" title={`${streak}-day streak`}>
          🔥 <b>{streak}</b>
        </span>

        <span
          className="sk-pill amber"
          title={`${xp} / ${xpToNextLevel} XP — ${xpPercent}% to level ${level + 1}`}
        >
          ⭐ <b>{xp.toLocaleString()}</b> · lv {level}
        </span>

        <span className="sk-pill" title={`${coins} coins`}>
          <span className="sk-coin">¢</span>
          <b>{coins}</b>
        </span>

        <button
          className={`sk-pill ${pendingInCount > 0 ? 'coral' : ''}`}
          style={{ cursor: 'pointer', border: '1.4px solid var(--ink)', fontFamily: 'inherit' }}
          onClick={() => navigate('/friends')}
          title={pendingInCount > 0 ? `${pendingInCount} pending friend request${pendingInCount === 1 ? '' : 's'}` : 'friends'}
        >
          👥{pendingInCount > 0 ? <b style={{ marginLeft: 4 }}>{pendingInCount}</b> : null}
        </button>

        <button
          className="sk-pill"
          style={{ cursor: 'pointer', border: '1.4px solid var(--ink)', fontFamily: 'inherit' }}
          onClick={() => navigate('/leaderboard')}
          title="daily leaderboard"
        >
          🏆
        </button>

        <button
          className="topbar-avatar"
          onClick={() => navigate('/profile')}
          title={`@${username}`}
          aria-label="View profile"
        >
          {username[0].toUpperCase()}
        </button>

        <button className="topbar-logout" onClick={onLogout}>
          out
        </button>
      </div>

      {/* Mobile hamburger — hidden on desktop via CSS */}
      <button
        className="topbar-burger"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
      >
        {menuOpen ? '✕' : '☰'}
      </button>

      {menuOpen && (
        <>
          <div
            className="topbar-scrim"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="topbar-sheet" role="dialog" aria-label="Menu">
            <div className="sheet-section">
              <div className="sheet-eyebrow">pages</div>
              <div className="sheet-links">
                {renderNavLinks(() => setMenuOpen(false))}
              </div>
            </div>

            <div className="sheet-section">
              <div className="sheet-eyebrow">your stats</div>
              <div className="sheet-pills">
                <span className="sk-pill" title={`${streak}-day streak`}>🔥 <b>{streak}</b></span>
                <span className="sk-pill amber">⭐ <b>{xp.toLocaleString()}</b> · lv {level}</span>
                <span className="sk-pill"><span className="sk-coin">¢</span> <b>{coins}</b></span>
              </div>
            </div>

            <div className="sheet-section">
              <div className="sheet-eyebrow">go to</div>
              <div className="sheet-links">
                <button
                  className="topbar-nav-link"
                  onClick={() => { setMenuOpen(false); onOpenSearch(); }}
                >🔍 search problems</button>
                <button
                  className="topbar-nav-link"
                  onClick={() => { setMenuOpen(false); navigate('/friends'); }}
                >👥 friends{pendingInCount > 0 ? ` · ${pendingInCount} new` : ''}</button>
                <button
                  className="topbar-nav-link"
                  onClick={() => { setMenuOpen(false); navigate('/leaderboard'); }}
                >🏆 leaderboard</button>
                <button
                  className="topbar-nav-link"
                  onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                >👤 @{username}</button>
              </div>
            </div>

            <button className="sk-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={onLogout}>
              sign out
            </button>
          </div>
        </>
      )}
    </header>
  );
};

export default TopBar;
